import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { toast } from 'react-hot-toast';
import { createApiUrl } from '../utils/config';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  // eslint-disable-next-line no-unused-vars
  const { user, isAuthenticated } = useAuth();
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const initializeTimeoutRef = useRef(null);
  const fetchingRef = useRef(false); // Track fetch status without causing re-renders
  const lastFetchTimeRef = useRef(0); // Track last fetch time to prevent rapid calls

  // Initialize cart when component mounts - ONLY ONCE
  useEffect(() => {
    if (isInitialized) return;
    
    console.log('🛒 CartProvider - initializing cart...');
    setIsInitialized(true);
    
    // Clear any pending timeout
    if (initializeTimeoutRef.current) {
      clearTimeout(initializeTimeoutRef.current);
    }
    
    // Delay initialization to prevent race conditions
    initializeTimeoutRef.current = setTimeout(() => {
      initializeCart();
    }, 100);
    
    return () => {
      if (initializeTimeoutRef.current) {
        clearTimeout(initializeTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - run only once

  // Handle user authentication changes - OPTIMIZED
  useEffect(() => {
    // Skip if not initialized yet
    if (!isInitialized) return;
    
    console.log('🛒 Auth status changed - isAuthenticated:', isAuthenticated);
    
    // Small delay to prevent rapid re-fetching
    const authChangeTimeout = setTimeout(async () => {
      if (fetchingRef.current) {
        console.log('🛒 Skip auth change - already fetching');
        return;
      }
      
      if (isAuthenticated) {
        console.log('🛒 User authenticated - fetching user cart');
      } else {
        console.log('🛒 User not authenticated - ensuring guest session');
        const guestSessionId = getSessionId();
        console.log('🛒 Guest session ID:', guestSessionId);
      }
      
      // Manually call cart fetch to avoid dependency issues
      try {
        // Create headers inline to avoid hoisting issues
        const headers = {
          'Content-Type': 'application/json',
        };

        if (isAuthenticated) {
          headers['Authorization'] = `Bearer ${localStorage.getItem('token')}`;
        } else {
          const currentSessionId = sessionId || localStorage.getItem('cart_session_id') || generateSessionId();
          if (!sessionId) {
            localStorage.setItem('cart_session_id', currentSessionId);
            setSessionId(currentSessionId);
          }
          headers['X-Session-ID'] = currentSessionId;
        }
        
        fetchingRef.current = true;
        setLoading(true);
        
        const response = await fetch(createApiUrl('api/cart'), {
          headers: headers,
          credentials: 'include'
        });

        if (response.ok) {
          const data = await response.json();
          const itemsCount = data.cart?.items?.length || 0;
          console.log(`🛒 Cart loaded successfully - ${itemsCount} items`);
          setCartItems(data.cart?.items || []);
        } else {
          console.error('❌ Failed to fetch cart, status:', response.status);
        }
      } catch (error) {
        console.error('❌ Error fetching cart:', error);
      } finally {
        fetchingRef.current = false;
        setLoading(false);
      }
    }, 300); // Increased delay to prevent rapid calls
    
    return () => clearTimeout(authChangeTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isInitialized]); // Remove getHeaders dependency to fix hoisting

  const getSessionId = () => {
    if (!sessionId) {
      const existingSessionId = localStorage.getItem('cart_session_id');
      const newSessionId = existingSessionId || generateSessionId();
      
      if (!existingSessionId) {
        console.log('🛒 Generated new session ID:', newSessionId);
        localStorage.setItem('cart_session_id', newSessionId);
      } else {
        console.log('🛒 Retrieved existing session ID:', newSessionId);
      }
      
      setSessionId(newSessionId);
      return newSessionId;
    }
    return sessionId;
  };

  const generateSessionId = () => {
    return 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  };

  const getHeaders = useCallback(() => {
    const headers = {
      'Content-Type': 'application/json',
    };

    if (isAuthenticated) {
      headers['Authorization'] = `Bearer ${localStorage.getItem('token')}`;
    } else {
      const currentSessionId = getSessionId();
      headers['X-Session-ID'] = currentSessionId;
    }

    return headers;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]); // Remove sessionId dependency to prevent loop

  const initializeCart = async () => {
    // Prevent multiple initialization
    if (fetchingRef.current) {
      console.log('🛒 Skip initialize - already fetching');
      return;
    }
    
    try {
      console.log('🛒 Initializing cart...');
      
      // Wait a moment for auth to initialize
      await new Promise(resolve => setTimeout(resolve, 50));
      
      if (isAuthenticated) {
        console.log('🛒 User is authenticated');
      } else {
        // For guest users, create session if not exists
        const guestSessionId = getSessionId();
        console.log('🛒 Guest session initialized:', guestSessionId);
      }
      
      // Manually fetch cart to avoid dependency issues
      try {
        // Create headers inline to avoid hoisting issues
        const headers = {
          'Content-Type': 'application/json',
        };

        if (isAuthenticated) {
          headers['Authorization'] = `Bearer ${localStorage.getItem('token')}`;
        } else {
          const currentSessionId = sessionId || localStorage.getItem('cart_session_id') || generateSessionId();
          if (!sessionId) {
            localStorage.setItem('cart_session_id', currentSessionId);
            setSessionId(currentSessionId);
          }
          headers['X-Session-ID'] = currentSessionId;
        }
        
        fetchingRef.current = true;
        setLoading(true);
        
        const response = await fetch(createApiUrl('api/cart'), {
          headers: headers,
          credentials: 'include'
        });

        if (response.ok) {
          const data = await response.json();
          const itemsCount = data.cart?.items?.length || 0;
          console.log(`🛒 Cart loaded successfully - ${itemsCount} items`);
          setCartItems(data.cart?.items || []);
        } else {
          console.error('❌ Failed to fetch cart, status:', response.status);
        }
      } catch (fetchError) {
        console.error('❌ Error fetching cart during initialization:', fetchError);
      } finally {
        fetchingRef.current = false;
        setLoading(false);
      }
    } catch (error) {
      console.error('❌ Error initializing cart:', error);
    }
  };

  const fetchCart = useCallback(async () => {
    const now = Date.now();
    
    if (fetchingRef.current) {
      console.log('🛒 Skip fetch - already loading');
      return;
    }
    
    // Prevent rapid successive calls (less than 1 second apart)
    if (now - lastFetchTimeRef.current < 1000) {
      console.log('🛒 Skip fetch - too soon since last fetch');
      return;
    }
    
    try {
      fetchingRef.current = true;
      lastFetchTimeRef.current = now;
      setLoading(true);
      const headers = getHeaders();
      console.log('🛒 Fetching cart...');
      
      const response = await fetch(createApiUrl('api/cart'), {
        headers: headers,
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        const itemsCount = data.cart?.items?.length || 0;
        console.log(`🛒 Cart loaded successfully - ${itemsCount} items`);
        setCartItems(data.cart?.items || []);
      } else {
        console.error('❌ Failed to fetch cart, status:', response.status);
        const errorData = await response.json();
        console.error('❌ Error details:', errorData);
      }
    } catch (error) {
      console.error('❌ Error fetching cart:', error);
    } finally {
      fetchingRef.current = false;
      setLoading(false);
    }
  }, [getHeaders]); // Remove loading dependency, add getHeaders

  const addToCart = async (product, quantity = 1, overridePrice = null) => {
    try {
      setLoading(true);
      
      // Check if product exists and has an id
      if (!product || !product.id) {
        console.error('❌ Product ID is required');
        toast.error('Product ID is required');
        return false;
      }

      console.log('🛒 Adding to cart:', product.name, 'quantity:', quantity, 'price:', overridePrice);

      const body = {
        product_id: product.id,
        quantity: quantity
      };
      if (overridePrice !== null && overridePrice !== undefined) {
        body.override_price = overridePrice;
      }

      const response = await fetch(createApiUrl('api/cart/add'), {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(body)
      });

      console.log('🛒 Add to cart response status:', response.status);
      if (response.ok) {
        const data = await response.json();
        console.log('🛒 Add to cart response data:', data);
        setCartItems(data.cart?.items || []);
        toast.success('Item added to cart!');
        return true;
      } else {
        const error = await response.json();
        console.error('❌ Add to cart error:', error);
        
        // Show more detailed error if available
        if (error.details) {
          console.error('❌ Error details:', error.details);
          if (!error.details.session_id_provided && !error.details.authenticated) {
            // Session ID is missing for guest user
            console.log('🛒 Session ID missing, regenerating...');
            const newSessionId = generateSessionId();
            localStorage.setItem('cart_session_id', newSessionId);
            setSessionId(newSessionId);
            toast.error('Session expired. Please try again.');
          } else {
            toast.error(error.error || 'Failed to add item to cart');
          }
        } else {
          toast.error(error.error || 'Failed to add item to cart');
        }
        return false;
      }
    } catch (error) {
      console.error('❌ Error adding to cart:', error);
      toast.error('Error adding item to cart');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const removeFromCart = async (itemId) => {
    try {
      setLoading(true);
      console.log('🛒 Removing from cart, item ID:', itemId);

      const response = await fetch(createApiUrl('api/cart/remove'), {
        method: 'DELETE',
        headers: getHeaders(),
        body: JSON.stringify({
          item_id: itemId
        })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('🛒 Remove from cart response data:', data);
        setCartItems(data.cart?.items || []);
        toast.success('Item removed from cart');
        return true;
      } else {
        const error = await response.json();
        console.error('❌ Remove from cart error:', error);
        toast.error(error.error || 'Failed to remove item from cart');
        return false;
      }
    } catch (error) {
      console.error('❌ Error removing from cart:', error);
      toast.error('Error removing item from cart');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = async (itemId, quantity) => {
    try {
      setLoading(true);
      console.log('🛒 Updating quantity, item ID:', itemId, 'quantity:', quantity);

      const response = await fetch(createApiUrl('api/cart/update'), {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify({
          item_id: itemId,
          quantity: quantity
        })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('🛒 Update quantity response data:', data);
        setCartItems(data.cart?.items || []);
        return true;
      } else {
        const error = await response.json();
        console.error('❌ Update quantity error:', error);
        toast.error(error.error || 'Failed to update quantity');
        return false;
      }
    } catch (error) {
      console.error('❌ Error updating quantity:', error);
      toast.error('Error updating quantity');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const clearCart = async () => {
    try {
      setLoading(true);
      console.log('🛒 Clearing cart...');

      const response = await fetch(createApiUrl('api/cart/clear'), {
        method: 'DELETE',
        headers: getHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        console.log('🛒 Clear cart response data:', data);
        setCartItems([]);
        toast.success('Cart cleared');
        return true;
      } else {
        const error = await response.json();
        console.error('❌ Clear cart error:', error);
        toast.error(error.error || 'Failed to clear cart');
        return false;
      }
    } catch (error) {
      console.error('❌ Error clearing cart:', error);
      toast.error('Error clearing cart');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Merge guest cart with user cart after login - kept for potential future use
  // eslint-disable-next-line no-unused-vars
  const mergeGuestCart = async () => {
    try {
      if (!sessionId) return;

      const response = await fetch(createApiUrl('api/cart/merge'), {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          session_id: sessionId
        })
      });

      if (response.ok) {
        const data = await response.json();
        setCartItems(data.cart?.items || []);
        // Clear session data
        localStorage.removeItem('cart_session_id');
        setSessionId(null);
        // Cart merged successfully
      }
    } catch (error) {
      console.error('❌ Error merging cart:', error);
    }
  };

  const getTotalItems = () => {
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  };

  const getTotalPrice = () => {
    return cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getCartItemCount = () => {
    return cartItems.length;
  };

  const isInCart = (productId) => {
    return cartItems.some(item => item.product_id === productId);
  };

  const getCartItem = (productId) => {
    return cartItems.find(item => item.product_id === productId);
  };

  const value = {
    cartItems,
    loading,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getTotalItems,
    getTotalPrice,
    getCartItemCount,
    isInCart,
    getCartItem,
    fetchCart,
    sessionId
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}; 